<template>
  <DxPopup
    v-if="visible"
    :visible="visible"
    width="100vw"
    height="100vh"
    :show-close-button="true"
    :hide-on-outside-click="false"
    :wrapper-attr="{ class: 'lot-editor-popup' }"
    @hiding="handleHiding"
    class="lot-editor-popup"
    :resize-enabled="true"
    :drag-enabled="false"
  >
    <template #title>
      <div class="lot-items-header">
        <div class="lot-items-title">{{ $t("lot_items.title") }}</div>
        <div class="lot-items-total">{{ $t("common.total") }}: {{ totalQuantity }} {{ measureUnitSymbol || "" }}</div>
      </div>
    </template>
    <template #content>
      <DxScrollView :use-native="true" class="lot-editor-content">
        <div v-if="!draftItems.length" class="lot-items-empty">
          {{ $t("lot_items.required") }}
        </div>
        <div v-if="getErrorMessage" class="lot-items-summary-error">
          {{ getErrorMessage() }}
        </div>
        <LotItemsEditor v-model="draftItems" :measure-unit-symbol="measureUnitSymbol" />
        <div class="lot-editor-actions">
          <button type="button" class="lot-editor-discard" @click="discard">
            {{ $t("lot_items.discard") }}
          </button>
          <button type="button" class="lot-editor-save" @click="save">
            {{ $t("lot_items.save") }}
          </button>
        </div>
      </DxScrollView>
    </template>
  </DxPopup>
</template>

<script>
import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxPopup } from "devextreme-vue/popup";
import LotItemsEditor from "@/components/Mobile/LotItemsEditor.vue";

export default {
  name: "LotItemsEditorPopup",
  components: {
    DxScrollView,
    DxPopup,
    LotItemsEditor,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    targetQuantity: {
      type: Number,
      default: 0,
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      draftItems: this.cloneItems(this.value),
      showError: false,
      closingBySave: false,
    };
  },
  computed: {
    isValid() {
      return this.isLotItemsValid(this.draftItems);
    },
    totalQuantity() {
      return parseFloat(this.draftItems.reduce((sum, item) => sum + this.toNumber(item.quantity), 0).toFixed(4));
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.draftItems = this.cloneItems(this.value);
        this.showError = false;
      }
    },
  },
  methods: {
    getErrorMessage() {
      if (!this.showError) {
        return "";
      }
      if (!Array.isArray(this.draftItems) || this.draftItems.length === 0) {
        return this.$t("lot_items.required");
      }
      if (this.hasInvalidRow(this.draftItems)) {
        return this.$t("lot_items.row_required");
      }
      return this.$t("lot_items.sum_mismatch");
    },
    cloneItems(items) {
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map((item) => ({
        lot: item.lot || "",
        expiration_date: item.expiration_date || null,
        quantity: this.toNumber(item.quantity, 0),
      }));
    },
    toNumber(value, def = 0) {
      const num = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(num) ? num : def;
    },
    isLotItemsValid(items) {
      if (!Array.isArray(items) || items.length === 0) {
        return false;
      }
      if (this.hasInvalidRow(items)) {
        return false;
      }
      const sum = items.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
      const qty = this.toNumber(this.targetQuantity, 0);
      return Math.abs(sum - qty) <= 0.0001 && qty > 0;
    },
    hasInvalidRow(items) {
      return items.some((item) => {
        const lot = (item.lot || "").trim();
        const expiration = item.expiration_date || null;
        const qty = this.toNumber(item.quantity, 0);
        return !lot || !expiration || qty <= 0;
      });
    },
    handleHiding(e) {
      if (this.closingBySave) {
        this.closingBySave = false;
        return;
      }
      if (!this.isLotItemsValid(this.draftItems)) {
        if (e && Object.prototype.hasOwnProperty.call(e, "cancel")) {
          e.cancel = true;
        }
        this.showError = true;
        return;
      }
    },
    discard() {
      this.$emit("update:visible", false);
    },
    save() {
      if (!this.isLotItemsValid(this.draftItems)) {
        this.showError = true;
        return;
      }
      this.closingBySave = true;
      this.$emit("save", this.cloneItems(this.draftItems));
      this.$emit("update:visible", false);
    },
  },
};
</script>

<style>
.lot-editor-popup .dx-popup-content {
  padding: 0px !important;
}
</style>

<style scoped>
.lot-items-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.lot-items-empty {
  font-size: 12px;
  color: #797979;
  margin-bottom: 4px;
}

.lot-items-title {
  font-weight: 700;
  font-size: 15px;
}

.lot-items-total {
  font-size: 12px;
  font-weight: 600;
  color: #3c3c3c;
}

.lot-editor-title {
  font-weight: 700;
  font-size: 16px;
}

.lot-editor-content {
  padding: 8px;
}

.lot-editor-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 32px 4px 24px 4px;
}

.lot-editor-discard {
  background: #ffffff;
  border: 1px solid #d1d1d1;
  color: #d06110;
  padding: 10px 12px;
  border-radius: 56px;
  font-weight: 600;
}

.lot-editor-save {
  background: #d06110;
  border: none;
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 56px;
  font-weight: 600;
}

.lot-editor-save:disabled {
  opacity: 0.6;
}

.lot-items-summary-error {
  margin-top: 6px;
  color: #dc3545;
  font-size: 12px;
}
</style>
